import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { get, post, del } from '@/lib/api-client'

interface Conversation {
  id: string
  contextType?: string
  createdAt: string
  updatedAt: string
  lastMessage?: string | null
}

interface ConversationMessage {
  id: string
  role: 'USER' | 'ASSISTANT'
  content: string
  createdAt: string
  metadata?: unknown
}

interface ConversationDetail {
  id: string
  tenantId: string
  status: string
  contextType?: string
  context?: Record<string, unknown>
  createdBy: string
  createdAt: string
  updatedAt: string
  messages: ConversationMessage[]
}

interface ConversationsResponse {
  conversations: Conversation[]
  pagination: {
    total: number
    page: number
    limit: number
  }
}

const v2Keys = {
  all: ['polix-ai-v2'] as const,
  conversations: () => [...v2Keys.all, 'conversations'] as const,
  conversation: (id: string) => [...v2Keys.conversations(), id] as const,
}

export function useV2Conversations() {
  return useQuery({
    queryKey: v2Keys.conversations(),
    queryFn: () => get<ConversationsResponse>('/v2/conversations'),
    staleTime: 30_000,
  })
}

export function useV2Conversation(id: string | undefined) {
  return useQuery({
    queryKey: v2Keys.conversation(id!),
    queryFn: () => get<ConversationDetail>(`/v2/conversations/${id}`),
    enabled: !!id,
    staleTime: 5_000,
    refetchOnWindowFocus: true,
  })
}

export { v2Keys }

export function useCreateV2Conversation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { contextType?: string; context?: object }) =>
      post<Conversation>('/v2/conversations', data),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: v2Keys.conversations() }),
  })
}

export function useDeleteV2Conversation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => del(`/v2/conversations/${id}`),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: v2Keys.conversations() }),
  })
}
